import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CookieService, SessionManagerService } from '@ops/shared-types';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { User } from 'legacy/shared/shared-types/src/lib/models/user.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OpsLanguageSwitchComponent } from '../ops-language-switch/ops-language-switch.component';

@Component({
  selector: 'ops-user-menu',
  standalone: true,
  imports: [CommonModule, TranslateModule, OpsLanguageSwitchComponent],
  templateUrl: './ops-user-menu.component.html',
  styleUrl: './ops-user-menu.component.scss',
})
export class OpsUserMenuComponent implements OnInit, OnDestroy {
  isExpanded = false;
  collapseTimeout: any;

  userFullName = '';
  userInitials = '';
  userEmail = '';
  userPhoneNumber = '';
  userPosition = '';
  userDescription = '';
  selectedLanguage = 'en';

  user: User;

  subscriptions: Array<Subscription> = [];

  constructor(
    private sessionManager: SessionManagerService,
    private router: Router,
    private translateService: TranslateService,
    private cookieService: CookieService
  ) {
    // Initialize selected language from cookie or current translation service
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    // Check if there's a saved language preference in cookies
    const savedLanguage = this.cookieService.getLanguage();

    if (savedLanguage && this.isValidLanguage(savedLanguage)) {
      // Use the saved language from cookie
      this.selectedLanguage = savedLanguage;
      this.translateService.setDefaultLang(savedLanguage);
      this.translateService.use(savedLanguage);
    } else {
      // Use current translation service language or fallback to 'en'
      this.selectedLanguage = this.translateService.currentLang || 'en';
    }
  }

  private isValidLanguage(language: string): boolean {
    const validLanguages = ['en', 'jp', 'sr']; //'de', 'jp', 'sr'
    return validLanguages.includes(language);
  }

  get isSettingsHidden(): boolean {
    const routerPath = this.router.url;

    return (
      !routerPath.includes('welcome') &&
      !routerPath.includes('/subscription') &&
      !routerPath.includes('stripe')
    );
  }

  onLanguageChange(lang: string) {
    this.selectedLanguage = lang;
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);

    // Save the language preference to cookies
    this.cookieService.setLanguage(lang);
  }

  ngOnInit(): void {
    const sub = this.sessionManager
      .getUserObservable()
      .subscribe((user: User) => {
        this.user = user;
        if (this.user) {
          if (user.firstName || user.lastName) {
            this.userInitials =
              (user.firstName[0]?.toUpperCase() ?? '') +
              (user.lastName[0]?.toUpperCase() ?? '');

            this.userFullName =
              (user.firstName + ' ' || '') + (user.lastName || '');

            this.userEmail = user.email;
            // this.userPhoneNumber = user.pho
          } else {
            // In case of first access after user being invited
            // we will print just email and first letter from email as
            // initials in order to avoid empty strings
            this.userInitials = user.email[0]?.toUpperCase() ?? '';
            this.userFullName = user.email;
          }
        } else {
          this.userInitials = '';
          this.userFullName = '';
          this.userEmail = '';
          this.isExpanded = false;
        }
      });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.cancelCollapseTimeout();
    this.isExpanded = false;
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  toggleCard(event: any) {
    // console.log('TOGGLE');
    event.stopPropagation();
    this.cancelCollapseTimeout();
    this.isExpanded = !this.isExpanded;
  }

  startCollapseTimeout() {
    this.cancelCollapseTimeout();
    this.collapseTimeout = setTimeout(() => {
      this.isExpanded = false;
    }, 3000);
  }

  cancelCollapseTimeout() {
    if (this.collapseTimeout) {
      clearTimeout(this.collapseTimeout);
    }
  }

  stopPropagation(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  onSelectAction(selection: string) {
    switch (selection.toLowerCase()) {
      case 'logout':
        {
          this.sessionManager.logout();
        }
        break;

      default:
        window.alert(
          this.translateService.instant('windowAlert.opsUserMenu.underConstruction')
        );
        break;
    }
  }

  openSettings() {
    this.router.navigate(['/settings/profile-settings']);
    this.isExpanded = false;
  }

  @HostListener('mouseover')
  onMouseOver() {
    this.cancelCollapseTimeout();
  }

  @HostListener('mouseout')
  onMouseOut() {
    this.cancelCollapseTimeout();
    if (this.isExpanded) {
      this.startCollapseTimeout();
    }
  }

  // Handle blur and close dropdown after a timeout
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.isExpanded) {
      this.isExpanded = false;
    }
  }
}
